/**
 * TanStack Query `queryOptions()` factories for the video domain, backed by
 * `services/VideoService.js`.
 */
import { queryOptions } from '@tanstack/react-query';
import {
    fetchVideosForTournament,
    fetchVideosForTeam,
    fetchVideoSources,
    fetchVideosByPlayerSlug,
    fetchVideo,
    fetchVideoComments,
    fetchLikedVideosForUser,
    getVideos
} from 'services/VideoService';

type Id = string | number;

export interface VideoListParams {
    page: number;
    pageSize?: number;
    unmatched?: boolean;
    sourceId?: Id;
}

export const videoQueries = {
    forTournament: (tournamentId: Id) =>
        queryOptions({
            queryKey: ['videos', 'byTournament', tournamentId],
            queryFn: () => fetchVideosForTournament(tournamentId),
            enabled: Boolean(tournamentId)
        }),

    forTeam: (teamSlug: string | undefined, pageSize?: number) =>
        queryOptions({
            queryKey: ['videos', 'byTeam', teamSlug, pageSize],
            queryFn: () => fetchVideosForTeam({ teamSlug, pageSize }),
            enabled: Boolean(teamSlug)
        }),

    byPlayerSlug: (slug: string | undefined, pageSize?: number) =>
        queryOptions({
            queryKey: ['videos', 'byPlayer', slug, pageSize],
            queryFn: () => fetchVideosByPlayerSlug({ slug, pageSize }),
            enabled: Boolean(slug)
        }),

    sources: () =>
        queryOptions({
            queryKey: ['videos', 'sources'],
            queryFn: fetchVideoSources
        }),

    byId: (videoId: Id | undefined) =>
        queryOptions({
            queryKey: ['videos', 'byId', videoId],
            queryFn: () => fetchVideo(videoId),
            enabled: Boolean(videoId)
        }),

    comments: (videoId: Id | undefined) =>
        queryOptions({
            queryKey: ['videos', 'comments', videoId],
            queryFn: () => fetchVideoComments(videoId),
            enabled: Boolean(videoId)
        }),

    // Rendered in ContentLayout on every page today and refetched on every
    // navigation with no caching -- the dedupe win called out in the plan.
    liked: () =>
        queryOptions({
            queryKey: ['videos', 'liked'],
            queryFn: fetchLikedVideosForUser
        }),

    // `getVideos` returns the raw Response (via apiFetchRaw) so admin/VideoList.jsx
    // can read the `X-Total-Count` header; the queryFn here does that once and
    // returns a plain, cacheable value.
    list: (params: VideoListParams) =>
        queryOptions({
            queryKey: [
                'videos',
                'list',
                params.page,
                params.pageSize,
                params.unmatched,
                params.sourceId
            ],
            queryFn: async () => {
                const response = await getVideos(params);
                const totalCount = Number(response.headers.get('X-Total-Count')) || 0;
                const data = await response.json();
                return { data, totalCount };
            }
        })
};
